import type { ReactNode } from "react"
import type {
  ModalOverlayProps as AriaModalOverlayProps,
  ModalRenderProps as AriaModalRenderProps,
} from "react-aria-components"

import {
  Dialog,
  Modal,
  ModalOverlay,
  SlideoutMenu as KitSlideoutMenu,
} from "@/components/ui-kit/untitled/application/slideout-menus/slideout-menu"

import { cx } from "@uui/utils/cx"

/**
 * The kit's SlideoutMenu hands one `className` to both its overlay and its
 * panel, so the scrim cannot be reached through it without painting the panel
 * too. This composes the same three kit parts and puts the scrim (a print.950
 * vignette, no blur — MDS D5) on the overlay alone; `className` stays the
 * panel's, as in the kit.
 */
interface SlideoutMenuProps extends Omit<
  AriaModalOverlayProps,
  "children" | "className"
> {
  children:
    | ReactNode
    | ((children: AriaModalRenderProps & { close: () => void }) => ReactNode)
  className?: string
  dialogClassName?: string
}

const Menu = ({
  children,
  className,
  dialogClassName,
  ...props
}: SlideoutMenuProps) => (
  <ModalOverlay {...props} className="scrim bg-transparent">
    <Modal className={cx(className)}>
      {(state) => (
        <Dialog className={dialogClassName}>
          {({ close }) =>
            typeof children === "function"
              ? children({ ...state, close })
              : children
          }
        </Dialog>
      )}
    </Modal>
  </ModalOverlay>
)

const SlideoutMenu: typeof Menu & {
  Trigger: typeof KitSlideoutMenu.Trigger
  Content: typeof KitSlideoutMenu.Content
  Header: typeof KitSlideoutMenu.Header
  Footer: typeof KitSlideoutMenu.Footer
} = Object.assign(Menu, {
  Trigger: KitSlideoutMenu.Trigger,
  Content: KitSlideoutMenu.Content,
  Header: KitSlideoutMenu.Header,
  Footer: KitSlideoutMenu.Footer,
})

export { SlideoutMenu }
